import { fetchAuthSession, signOut } from "aws-amplify/auth";
import { env } from "@/lib/env";
import { useAuthStore } from "@/stores/auth-store";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public errors?: unknown[],
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  authenticated?: boolean;
  /** HTTP statuses that the caller handles as part of normal control
   *  flow (e.g. 404 on a workbench stream that wasn't recorded, 409 on
   *  /plot before parquet is ready). The ``ApiError`` is still thrown so
   *  the caller can branch on ``err.status`` / ``err.code``, but the
   *  console.error log is suppressed to keep the DevTools console clean. */
  expectedStatuses?: number[];
}

async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

async function handleUnauthorized(): Promise<never> {
  try {
    await signOut();
  } finally {
    useAuthStore.getState().clearAuth();
  }
  throw new ApiError(401, "UNAUTHORIZED", "Session expired");
}

/** OHTIS error bodies use `{ "error": { "code", "message" } }` (see shared/utils/response.py). */
function parseErrorPayload(raw: unknown): {
  code: string;
  message: string;
  errors?: unknown[];
} {
  if (!raw || typeof raw !== "object") {
    return { code: "UNKNOWN_ERROR", message: "" };
  }
  const o = raw as Record<string, unknown>;
  const inner =
    o.error != null && typeof o.error === "object"
      ? (o.error as Record<string, unknown>)
      : o;
  const code =
    typeof inner.code === "string" ? inner.code : "UNKNOWN_ERROR";
  const message =
    typeof inner.message === "string"
      ? inner.message
      : typeof o.message === "string"
        ? o.message
        : "";
  const errors = Array.isArray(inner.errors) ? inner.errors : undefined;
  return { code, message, errors };
}

export async function apiClient<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const {
    body,
    authenticated = true,
    headers: extraHeaders,
    expectedStatuses,
    ...rest
  } = options;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(extraHeaders as Record<string, string>),
  };

  if (authenticated) {
    const token = await getAuthToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  const url = `${env.apiBaseUrl}${path}`;

  const response = await fetch(url, {
    ...rest,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    if (response.status === 401 && authenticated) {
      return handleUnauthorized();
    }

    let rawError: unknown;
    try {
      rawError = await response.json();
    } catch {
      rawError = {};
    }

    const { code, message, errors } = parseErrorPayload(rawError);

    const isExpected = expectedStatuses?.includes(response.status) ?? false;
    if (import.meta.env.DEV && !isExpected) {
      console.error(`[API ${response.status}] ${options.method ?? "GET"} ${path}`, rawError);
    }

    throw new ApiError(
      response.status,
      code,
      message || response.statusText,
      errors,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const json = await response.json();
  const data =
    json && typeof json === "object" && "data" in json ? json.data : json;
  return data as T;
}
